"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import {
  MdArrowForward,
  MdOutlineChevronRight,
  MdOutlineChevronLeft,
} from "react-icons/md";
import { FaLocationDot } from "react-icons/fa6";
import {
  MdBed,
  MdBathtub,
  MdSquareFoot,
  MdFavoriteBorder,
} from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./editors-picks.module.css";

const PROPERTIES = [
  {
    id: "p1",
    title: "Oceanfront Masterpiece",
    location: "Bali, Indonesia",
    price: "€ 18.9M",
    description:
      "A stunning 6-bedroom villa with infinity pool, private beach access and panoramic ocean views.",
    beds: 6,
    baths: 7,
    sqm: 660,
    image: "/placeholder-images/placeholder1.jpg",
    agentName: "Andrei Popescu",
    agencyName: "Prestige Estates",
    badge: "FEATURED RESIDENCE",
  },
  {
    id: "p2",
    title: "Ski-In Chalet",
    location: "Zermatt, Switzerland",
    price: "€ 7.2M",
    description:
      "An exclusive alpine retreat featuring ski-in/ski-out access, a heated indoor pool, and majestic Matterhorn views.",
    beds: 5,
    baths: 6,
    sqm: 420,
    image: "/placeholder-images/placeholder2.jpg",
    agentName: "Elena Ionescu",
    agencyName: "Global Luxury",
    badge: "EDITOR'S PICK",
  },
  {
    id: "p3",
    title: "Private Island Resort",
    location: "Malé, Maldives",
    price: "€ 24.5M",
    description:
      "Your own slice of paradise. Crystal clear waters, overwater bungalows, and complete unmatched privacy.",
    beds: 8,
    baths: 9,
    sqm: 1200,
    image: "/placeholder-images/placeholder3.jpg",
    agentName: "Vlad Matei",
    agencyName: "Island Homes",
    badge: "MOST VIEWED",
  },
  {
    id: "p4",
    title: "Modern Penthouse",
    location: "Dubai, UAE",
    price: "€ 12.5M",
    description:
      "Soar above the city in this ultra-modern penthouse featuring floor-to-ceiling glass and a private rooftop terrace.",
    beds: 4,
    baths: 4,
    sqm: 320,
    image: "/placeholder-images/placeholder4.jpg",
    agentName: "Sarah Smith",
    agencyName: "Desert Luxury",
    badge: "NEW ARRIVAL",
  },
  {
    id: "p5",
    title: "Tuscan Country Estate",
    location: "Tuscany, Italy",
    price: "€ 3.6M",
    description:
      "A beautifully restored 18th-century farmhouse surrounded by rolling vineyards and olive groves.",
    beds: 6,
    baths: 4,
    sqm: 450,
    image: "/placeholder-images/countryside-living.png",
    agentName: "Marco Rossi",
    agencyName: "Italian Villas",
    badge: "EXCLUSIVE",
  },
  {
    id: "p6",
    title: "Bel Air Mansion",
    location: "Los Angeles, USA",
    price: "€ 32.0M",
    description:
      "The pinnacle of luxury living. Features a 20-car garage, home theater, and expansive views of the LA basin.",
    beds: 9,
    baths: 12,
    sqm: 1800,
    image: "/placeholder-images/city-living.png",
    agentName: "James Carter",
    agencyName: "Beverly Hills Real Estate",
    badge: "FEATURED",
  },
];

export default function EditorsPicks() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setHasDragged(false); // <-- Reset on new click
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };
  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 50); // <-- Delay reset to let the click handler fire and be blocked
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;

    // <-- If they move the mouse more than 5px, it's a drag, not a click
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }

    scrollRef.current.scrollLeft = scrollLeft - walk;
  };
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % PROPERTIES.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + PROPERTIES.length) % PROPERTIES.length,
    );
  };

  const handleSelect = (indexToSelect: number) => {
    setCurrentIndex(indexToSelect);
  };

  const featured = PROPERTIES[currentIndex];

  const queueItems = [
    PROPERTIES[(currentIndex + 1) % PROPERTIES.length],
    PROPERTIES[(currentIndex + 2) % PROPERTIES.length],
    PROPERTIES[(currentIndex + 3) % PROPERTIES.length],
  ];

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>Editor&apos;s Picks</h2>
          <p className={styles.subtitle}>
            A curated selection of the world&apos;s most exceptional properties.
          </p>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.updateBadge}>Updated every 72 hours</span>
          <Link href="/search?type=luxury" className={styles.viewAllLink}>
            View collection
            <MdArrowForward className={styles.linkIcon} />
          </Link>
        </div>
      </div>

      <div className={styles.cinematicContainer}>
        {/* TOP: MASSIVE HERO CARD */}
        <div className={styles.heroWrapper}>
          <button
            className={`${styles.navArrow} ${styles.navLeft}`}
            onClick={handlePrev}
            aria-label="Previous"
          >
            <MdOutlineChevronLeft />
          </button>
          <button
            className={`${styles.navArrow} ${styles.navRight}`}
            onClick={handleNext}
            aria-label="Next"
          >
            <MdOutlineChevronRight />
          </button>

          <AnimatePresence initial={false}>
            <motion.div
              key={featured.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className={styles.heroCard}
            >
              <Image
                src={featured.image}
                alt={featured.title}
                fill
                priority
                sizes="(max-width: 768px) 100vw, 90vw"
                className={styles.heroImage}
              />

              <div className={styles.heroOverlay}>
                {/* FLOATING AGENT WIDGET (Top Left) */}
                <div className={styles.featuredAgent}>
                  <div className={styles.agentAvatarFallback} />
                  <div className={styles.agentDetails}>
                    <span className={styles.agentName}>
                      {featured.agentName}
                    </span>
                    <span className={styles.agencyName}>
                      {featured.agencyName}
                    </span>
                  </div>
                </div>

                <div className={styles.heroContentSplit}>
                  {/* Property Data */}
                  <div className={styles.heroData}>
                    <div className={styles.badgeAndTitle}>
                      <span className={styles.heroBadge}>{featured.badge}</span>

                      <div className={styles.heroTitleGroup}>
                        <h3 className={styles.heroTitle}>{featured.title}</h3>
                        <div className={styles.location}>
                          <FaLocationDot className={styles.locationIcon} />
                          <span>{featured.location}</span>
                        </div>
                      </div>
                    </div>

                    <p className={styles.heroDescription}>
                      {featured.description}
                    </p>

                    <div className={styles.heroDivider} />

                    <div className={styles.heroPriceGroup}>
                      <div className={styles.heroPrice}>{featured.price}</div>
                      <div className={styles.amenities}>
                        <div className={styles.amenityItem}>
                          <MdBed /> <span>{featured.beds} Beds</span>
                        </div>
                        <div className={styles.amenityItem}>
                          <MdBathtub /> <span>{featured.baths} Baths</span>
                        </div>
                        <div className={styles.amenityItem}>
                          <MdSquareFoot /> <span>{featured.sqm} sqm</span>
                        </div>
                      </div>
                    </div>

                    <div className={styles.heroActionRow}>
                      <Link
                        href={`/listing/${featured.id}`}
                        className={styles.viewBtn}
                      >
                        View Property <MdArrowForward />
                      </Link>
                      <button
                        className={styles.favoriteBtn}
                        aria-label="Save Property"
                      >
                        <MdFavoriteBorder />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* BOTTOM: THE VIP DARK BAR QUEUE */}
        <div className={styles.darkQueueBar}>
          <div className={styles.queueHeader}>
            <h4 className={styles.queueTitle}>
              ALSO IN THIS
              <br />
              COLLECTION
            </h4>
            <div className={styles.queueLine} />
          </div>

          <div
            className={`${styles.queueTrack} ${isDragging ? styles.dragging : ""}`}
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {queueItems.map((item, index) => {
              const originalIndex = PROPERTIES.findIndex(
                (p) => p.id === item.id,
              );

              return (
                <button
                  key={`${item.id}-${currentIndex}`}
                  onClickCapture={(e) => {
                    if (hasDragged) {
                      e.stopPropagation();
                      e.preventDefault();
                    } else {
                      handleSelect(originalIndex);
                    }
                  }}
                  className={`${styles.queueItem} ${index === 2 ? styles.hideOnTablet : ""}`}
                >
                  <div className={styles.queueItemImageWrapper}>
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      sizes="(max-width: 768px) 80vw, (max-width: 1024px) 30vw, 15vw"
                      className={styles.queueItemImage}
                      draggable={false}
                    />
                  </div>

                  <div className={styles.queueItemData}>
                    <div className={styles.queueItemTop}>
                      <h5 className={styles.queueItemTitle}>{item.title}</h5>
                      <div className={styles.queueLocationWrapper}>
                        <FaLocationDot className={styles.queueLocationIcon} />
                        <span className={styles.queueItemLocation}>
                          {item.location}
                        </span>
                      </div>
                    </div>

                    <div className={styles.queueItemBottom}>
                      <span className={styles.queueItemPrice}>
                        {item.price}
                      </span>
                      <div className={styles.amenitiesSmall}>
                        <div className={styles.amenityItemSmall}>
                          <MdBed /> <span>{item.beds}</span>
                        </div>
                        <div className={styles.amenityItemSmall}>
                          <MdBathtub /> <span>{item.baths}</span>
                        </div>
                        <div className={styles.amenityItemSmall}>
                          <MdSquareFoot /> <span>{item.sqm} sqm</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
